import { type RefObject, useEffect } from "react";
import * as THREE from "three/webgpu";
import { selectionTargetLeafIds } from "../app/objectSelection.ts";
import type {
  AppState,
  BlendFileEntry,
  Collection,
  ObjectSelectionTarget,
} from "../app/storeTypes.ts";
import { hashObjectIdToColor } from "./objectIdColor.ts";

export const SELECTION_OUTLINE_LAYER = 4;
export const SELECTION_OUTLINE_COLOR = "#f5a623";
export const ACTIVE_SELECTION_OUTLINE_COLOR = "#ffd15c";
export const GENERATOR_SELECTION_OUTLINE_COLOR = "#82d96e";
export const ACTIVE_GENERATOR_SELECTION_OUTLINE_COLOR = "#b8ffb4";
export const INSTANCE_SELECTION_OUTLINE_COLOR = "#7fdcc2";
export const ACTIVE_INSTANCE_SELECTION_OUTLINE_COLOR = "#b8fff0";

export type SelectionOutlineVariant = "default" | "generator" | "instance";

// Alpha values encode the variant in the selection prepass. Three bands are used so
// the post-processing pass can distinguish them via smoothstep thresholds:
//   default:   selected=0.25, active=0.42  (low band)
//   generator: selected=0.57, active=0.70  (mid band)
//   instance:  selected=0.82, active=0.95  (high band)
const ALPHA_BY_VARIANT: Record<
  SelectionOutlineVariant,
  { selected: number; active: number }
> = {
  default: { selected: 0.25, active: 0.42 },
  generator: { selected: 0.57, active: 0.7 },
  instance: { selected: 0.82, active: 0.95 },
};

// Nested cache — outer key is objectId, inner key is "variant:active". This
// lets us dispose every material for a deleted object in one pass without
// string-parsing (object ids may themselves contain colons).
const materialCache = new Map<string, Map<string, THREE.MeshBasicMaterial>>();

function variantKey(variant: SelectionOutlineVariant, active: boolean): string {
  return `${variant}:${active ? "active" : "selected"}`;
}

function createSelectionPrepassMaterial(
  objectId: string,
  variant: SelectionOutlineVariant,
  active: boolean,
): THREE.MeshBasicMaterial {
  const color = hashObjectIdToColor(objectId);
  const alpha = active
    ? ALPHA_BY_VARIANT[variant].active
    : ALPHA_BY_VARIANT[variant].selected;
  const mat = new THREE.MeshBasicMaterial({
    transparent: true,
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
    blending: THREE.NoBlending,
    color,
    opacity: alpha,
  });
  mat.name = `SelectionPrepass:${variant}:${active ? "active" : "selected"}:${objectId}`;
  return mat;
}

export function getSelectionPrepassMaterial(
  objectId: string,
  variant: SelectionOutlineVariant,
  active: boolean,
): THREE.MeshBasicMaterial {
  let perObject = materialCache.get(objectId);
  if (!perObject) {
    perObject = new Map();
    materialCache.set(objectId, perObject);
  }
  const key = variantKey(variant, active);
  let material = perObject.get(key);
  if (!material) {
    material = createSelectionPrepassMaterial(objectId, variant, active);
    perObject.set(key, material);
  }
  return material;
}

/**
 * Drop cached prepass materials whose owning object is no longer alive.
 * Called from the viewport's scene-delete subscription; without this the
 * cache grew unbounded across selection cycles.
 */
export function pruneSelectionPrepassMaterials(
  aliveIds: ReadonlySet<string>,
): void {
  for (const [id, perObject] of materialCache) {
    if (aliveIds.has(id)) continue;
    for (const mat of perObject.values()) mat.dispose();
    materialCache.delete(id);
  }
}

/** Test-only: dispose and reset the entire material cache. */
export function __resetSelectionPrepassMaterialCache(): void {
  for (const perObject of materialCache.values()) {
    for (const mat of perObject.values()) mat.dispose();
  }
  materialCache.clear();
}

/**
 * Return the "active" scene-object id — the one that should render with
 * the highlighted outline variant. Defined as the last-added entry in
 * the selection target list, walked through to its leaf ids in the same
 * order. `sceneObject` and `blendEntry` targets expand naturally;
 * `collection` targets pick the last member.
 */
export function getActiveSelectedObjectId(
  targets: readonly ObjectSelectionTarget[],
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  collections: ReadonlyMap<string, Collection>,
): string | null {
  if (targets.length === 0) return null;
  const active = targets[targets.length - 1];
  const leaves = selectionTargetLeafIds(blendFiles, collections, active);
  return leaves.length > 0 ? leaves[leaves.length - 1] : null;
}

// Module-level memo so every viewport node that asks for the active id
// gets the same string per state — avoids per-consumer re-derivation
// fanning out across primitives, blend meshes, mannequins, lights, etc.
let cachedActive: {
  targets: AppState["selectedObjectTargets"];
  blendFiles: AppState["blendFiles"];
  collections: AppState["collections"];
  result: string | null;
} | null = null;

export function getActiveSelectedObjectIdFromState(
  state: AppState,
): string | null {
  if (
    cachedActive &&
    cachedActive.targets === state.selectedObjectTargets &&
    cachedActive.blendFiles === state.blendFiles &&
    cachedActive.collections === state.collections
  ) {
    return cachedActive.result;
  }
  const result = getActiveSelectedObjectId(
    state.selectedObjectTargets,
    state.blendFiles,
    state.collections,
  );
  cachedActive = {
    targets: state.selectedObjectTargets,
    blendFiles: state.blendFiles,
    collections: state.collections,
    result,
  };
  return result;
}

/** Test-only: drop the module-level cache between store instances. */
export function __resetActiveSelectedObjectIdCache(): void {
  cachedActive = null;
}

export function useSelectionOutlineLayer(
  meshRef: RefObject<THREE.Object3D | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || !enabled) return;
    mesh.layers.set(SELECTION_OUTLINE_LAYER);
    return () => {
      mesh.layers.disable(SELECTION_OUTLINE_LAYER);
    };
  }, [meshRef, enabled]);
}
